// ─── opening a session ───────────────────────────────────────────────────────
// `POST /v1/sessions` is a control-plane call and takes an API key, which a browser
// must never hold (docs/SERVICE_API.md §Auth). So this posts to a *broker*:
//   · dev  — `wrangler dev` with DEV_OPEN=true, which skips API-key auth
//   · prod — the same-origin Pages Function in `functions/v1/sessions.ts`, which
//            adds the key server-side and forwards to the service
// Either way the browser receives only `wsUrl`, `placement` and `clientConfig`.

import type { ClientProviderConfig, Placement, VisualInputCapability } from '@shared/protocol.ts'
import type { SessionConfig, ToolDecl, ToolExec } from '@shared/session.ts'

export interface OpenedSession {
  sessionId: string
  token: string
  expiresIn: number
  wsUrl: string
  protocol: number
  provider: string
  placement: Placement
  clientConfig?: ClientProviderConfig
  capabilities?: { visualInput?: VisualInputCapability }
}

/** A refused or broken create call, with enough detail to put on screen. */
export class SessionError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly details: string[] = [],
  ) {
    super(message)
    this.name = 'SessionError'
  }
}

/**
 * A tool as a *caller* writes it. Builtins may omit description and parameters —
 * `validateSessionConfig` fills them in from `BUILTIN_TOOLS`, so the contract cannot
 * drift from the implementation.
 */
export type ToolRequest = Partial<ToolDecl> & { name: string; exec: ToolExec }

export type SessionRequest = Partial<
  Pick<SessionConfig, 'persona' | 'voice' | 'locale' | 'turnTaking' | 'greeting' | 'maxDurationMs' | 'ref' | 'audio'>
> & {
  provider: { id: string; placement?: Placement; options?: Record<string, unknown> }
  tools?: ToolRequest[]
}

export async function openSession(url: string, request: SessionRequest, signal?: AbortSignal): Promise<OpenedSession> {
  let res: Response
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(request),
      signal,
    })
  } catch (err) {
    // A dead broker is the most likely first-run failure, so name it precisely.
    throw new SessionError(`could not reach ${url}`, 0, [err instanceof Error ? err.message : String(err)])
  }

  const body = (await res.json().catch(() => null)) as
    | (OpenedSession & { error?: string; details?: string[]; hint?: string })
    | null

  if (!res.ok || !body?.wsUrl) {
    const details = [...(body?.details ?? []), ...(body?.hint ? [body.hint] : [])]
    throw new SessionError(body?.error ?? `session refused (HTTP ${res.status})`, res.status, details)
  }
  return body
}
